import { ArrowRight, Github, Globe, Link2, Linkedin, Mail, MapPin, Phone } from "lucide-react";
import { cn } from "@/lib/utils";

export const initials = (n) => (n || "?").trim().split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
const nameOf = (s) => (typeof s === "string" ? s : s?.name || "");
const certText = (c) => (typeof c === "string" ? c : `${[c.name, c.issuer].filter(Boolean).join(" · ")}${c.year ? ` · ${c.year}` : ""}`);
const href = (v) => (v.startsWith("http") ? v : `https://${v}`);
const LINK_ICON = { linkedin: Linkedin, github: Github, portfolio: Globe, website: Link2 };
const topSkills = (p) => (p.skill_categories?.length ? p.skill_categories.flatMap((c) => c.skills || []) : p.skills || []).map(nameOf).filter(Boolean).slice(0, 4);

function Side({ title, children, testId }) {
  return <section className="rounded-2xl border border-border bg-card p-5 space-y-3" data-testid={testId}><h2 className="text-sm text-muted-foreground">{title}</h2>{children}</section>;
}

function Skills({ p }) {
  if (p.skill_categories?.length) return (
    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
      {p.skill_categories.map((cat, i) => (
        <div key={i} className="rounded-2xl border border-border bg-card p-4 space-y-2 min-w-0">
          <p className="text-sm font-medium text-primary">{cat.category}</p>
          <ul className="space-y-1.5">
            {(cat.skills || []).map((s, j) => (
              <li key={j} className="flex items-baseline justify-between gap-3 text-sm">
                <span className="min-w-0 [overflow-wrap:anywhere]">{nameOf(s)}</span>
                {s?.proficiency && <span className={cn("text-xs shrink-0", /^expert/i.test(s.proficiency) ? "text-primary" : "text-muted-foreground")}>{s.proficiency}</span>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
  const list = [...(p.skills || []), ...(p.tools || [])].map(nameOf).filter(Boolean);
  return <div className="flex flex-wrap gap-2">{list.map((s, i) => <span key={i} className="rounded-full border border-border px-3 py-1 text-sm">{s}</span>)}</div>;
}

function Timeline({ items, testId }) {
  return (
    <ol className="divide-y divide-border border-t border-border" data-testid={testId}>
      {items.map((e, i) => (
        <li key={i} className="grid sm:grid-cols-[130px_minmax(0,1fr)] gap-x-6 gap-y-1 py-4">
          <span className="hs-mono text-xs text-muted-foreground pt-1">{e.period}</span>
          <div className="min-w-0 space-y-1">
            <p className="font-semibold [overflow-wrap:anywhere]">{[e.title || e.name, e.company].filter(Boolean).join(" · ")}</p>
            {(e.summary || e.description) && <p className="text-sm leading-6 text-muted-foreground">{e.summary || e.description}</p>}
            {e.highlights?.length > 0 && <ul className="space-y-1">{e.highlights.map((h, j) => <li key={j} className="flex gap-2 text-sm leading-6 text-muted-foreground"><span className="mt-2.5 h-1 w-1 rounded-full bg-primary shrink-0" />{h}</li>)}</ul>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function Contact({ p }) {
  const ct = p.contact || {};
  const links = Object.entries(p.links || {}).filter(([, v]) => v);
  if (!ct.email && !ct.phone && !links.length) return null;
  const row = "flex items-center gap-2.5 text-sm min-w-0";
  return (
    <Side title="Contact" testId="shared-contact">
      {ct.email && <a href={`mailto:${ct.email}`} className={cn(row, "hover:text-primary")}><Mail className="h-4 w-4 text-muted-foreground shrink-0" /><span className="truncate">{ct.email}</span></a>}
      {ct.phone && <p className={row}><Phone className="h-4 w-4 text-muted-foreground shrink-0" />{ct.phone}</p>}
      {links.map(([k, v]) => { const Icon = LINK_ICON[k] || Link2; return <a key={k} href={href(v)} target="_blank" rel="noreferrer" className={cn(row, "text-primary hover:underline")}><Icon className="h-4 w-4 shrink-0" /><span className="truncate">{v.replace(/^https?:\/\//, "").replace(/\/$/, "")}</span></a>; })}
    </Side>
  );
}

export function SharedCV({ c }) {
  const p = c.profile || {};
  const meta = [c.headline || p.headline, p.recruitment_intelligence?.total_experience, p.contact?.location].filter(Boolean);
  const hasSkills = p.skill_categories?.length || p.skills?.length || p.tools?.length;
  return (
    <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_300px]" data-testid="shared-cv">
      <div className="min-w-0 space-y-9">
        <header className="flex items-center gap-5 min-w-0">
          <span className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-primary/15 text-primary flex items-center justify-center font-display text-2xl font-semibold shrink-0">{initials(c.name)}</span>
          <div className="min-w-0">
            <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight [overflow-wrap:anywhere]" data-testid="shared-name">{c.name}</h1>
            {meta.length > 0 && <p className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-1.5">{p.contact?.location && <MapPin className="h-3.5 w-3.5" />}{meta.join(" · ")}</p>}
          </div>
        </header>
        {!c.profile && <p className="text-sm text-muted-foreground">This candidate has not published a full CV profile yet.</p>}
        {p.summary && <p className="text-base leading-8 text-foreground/85" data-testid="shared-summary">{p.summary}</p>}
        {hasSkills ? <section className="space-y-3" data-testid="shared-skills"><h2 className="font-display text-lg font-semibold">Skill matrix</h2><Skills p={p} /></section> : null}
        {p.experience?.length > 0 && <section className="space-y-3"><h2 className="font-display text-lg font-semibold">Experience</h2><Timeline items={p.experience} testId="shared-experience" /></section>}
        {p.projects?.length > 0 && <section className="space-y-3"><h2 className="font-display text-lg font-semibold">Projects</h2><Timeline items={p.projects} testId="shared-projects" /></section>}
      </div>
      {c.profile && (
        <aside className="space-y-4 min-w-0">
          <Contact p={p} />
          {p.certifications?.length > 0 && <Side title="Certifications" testId="shared-certifications"><ul className="space-y-1.5 text-sm">{p.certifications.map((x, i) => <li key={i}>{certText(x)}</li>)}</ul></Side>}
          {p.education?.length > 0 && <Side title="Education" testId="shared-education">{p.education.map((e, i) => <div key={i} className="text-sm"><p>{[e.degree, e.field].filter(Boolean).join(", ")}</p><p className="text-xs text-muted-foreground">{[e.institution, e.period].filter(Boolean).join(" · ")}</p></div>)}</Side>}
          {p.languages?.length > 0 && <Side title="Languages"><div className="flex flex-wrap gap-2">{p.languages.map((l, i) => <span key={i} className="rounded-full border border-border px-3 py-1 text-sm">{nameOf(l)}</span>)}</div></Side>}
        </aside>
      )}
    </div>
  );
}

export function ShortlistCard({ c, i, onOpen }) {
  const p = c.profile || {};
  const skills = topSkills(p);
  const rows = [["Experience", p.recruitment_intelligence?.total_experience], ["Location", p.contact?.location]].filter(([, v]) => v);
  return (
    <article className="rounded-2xl border border-border bg-card p-5 flex flex-col gap-4 min-w-0" data-testid={`shared-candidate-${i}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className="h-11 w-11 rounded-full bg-primary/15 text-primary flex items-center justify-center font-semibold shrink-0">{initials(c.name)}</span>
        <div className="min-w-0">
          <p className="font-display text-lg font-semibold truncate">{c.name}</p>
          {(c.headline || p.headline) && <p className="text-sm text-muted-foreground truncate">{c.headline || p.headline}</p>}
        </div>
      </div>
      {p.summary && <p className="text-sm leading-6 text-foreground/80 line-clamp-3">{p.summary}</p>}
      {rows.length > 0 && <dl className="space-y-1 text-sm">{rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><dt className="text-muted-foreground">{k}</dt><dd className="text-right">{v}</dd></div>)}</dl>}
      {skills.length > 0 && <div className="flex flex-wrap gap-1.5">{skills.map((s, j) => <span key={j} className="rounded-full border border-border px-2.5 py-0.5 text-xs">{s}</span>)}</div>}
      {c.profile
        ? <button onClick={onOpen} className="mt-auto self-start text-sm font-medium text-primary inline-flex items-center gap-1 hover:underline" data-testid={`shared-open-${i}`}>Open full profile<ArrowRight className="h-3.5 w-3.5" /></button>
        : <p className="mt-auto text-xs text-muted-foreground">No full CV profile published yet.</p>}
    </article>
  );
}
